import {ProductSearchFieldsEnum, ProductSortOrderEnum, ProductSortTypeEnum} from "./productSearchFieldsEnum.js";
import {ProductResultsEnum} from "./productResultsEnum.js";
import {ConfigService, util, BrowserDetection} from '@nuskin/ns-util';
import {AccountManager} from '@nuskin/ns-account';
import SolrQueryService from "./SolrQueryService.js";
import SynonymService from "./synonymService";
import {PriceType, Product} from '@nuskin/ns-product-lib';
import OrderType from "./orderType.js";

let productSearchService = function() {
    let gpfslCount = 0;
    //---------------------------------------------------------------------------
    //
    //                           Public Methods
    //
    //---------------------------------------------------------------------------
    let publicMethods = {

        /**
         * Quick Search (Type Ahead Search)
         *
         * @param {string} searchTerm - Examples: Age, Age*, *Age*, etc.
         * @param {number[]} [productFields=[1,2,3,6,7,8,9,10,11]] - Examples: ProductSearchFieldsEnum.TITLE, ProductSearchFieldsEnum.SKU
         * @param {number} [limit=6]   - Example: 5
         * @param {string} [market=util.countryCode] - Example: "US"
         * @param {string} [language=util.languageCode]   - Example: "en"
         * @param {number} [productResultTypes=1] - Example: ProductResultsEnum.PRODUCTS_AND_BASE_PRODUCTS -- default: ProductResultsEnum.ALL_PRODUCTS
         * @param {string[]=} orderTypes - ** Using this parameter will force two queries to Solr -- PLEASE DON'T USE UNLESS YOU NEED IT AND YOU ARE
         *          AWARE THERE WILL BE AN INCREASED LOAD AND LESS PERFORMANCE **  Example: [OrderType.ADR, OrderType.ORDER, OrderType.ZPO]
         * @returns A promise to the result which contains a list of products that match the searchTerm for the field type
         *          (ordered by count of occurrences).
         */
        quickSearch: function(searchTerm, productFields, limit, market, language, productResultTypes, orderTypes) {
            if (typeof(productFields) === "undefined" || productFields === null) {
                productFields = [];
                productFields.push(ProductSearchFieldsEnum.SKU);
                productFields.push(ProductSearchFieldsEnum.TITLE);
                productFields.push(ProductSearchFieldsEnum.DESCRIPTION);
                productFields.push(ProductSearchFieldsEnum.LONG_DESCRIPTION);
                productFields.push(ProductSearchFieldsEnum.CATEGORIES);
                productFields.push(ProductSearchFieldsEnum.SEARCH_TERMS);
                productFields.push(ProductSearchFieldsEnum.BENEFITS);
                productFields.push(ProductSearchFieldsEnum.USAGE);
                productFields.push(ProductSearchFieldsEnum.KEY_INGREDIENTS);
            }
            if (typeof(limit) === "undefined"|| limit === null)
                limit = 6;
            if (typeof(market) === "undefined" || market === null) {
                market = util.countryCode;
            }
            if (typeof(language) === "undefined" || language === null) {
                language = util.languageCode;
            }
            if (typeof(productResultTypes) === "undefined" || productResultTypes === null)
                productResultTypes = ProductResultsEnum.ALL_PRODUCTS;

            searchTerm = removeSpecialCharacters(SynonymService.getSynonym(searchTerm));
            return new Promise((resolve, reject) => {
                SolrQueryService.getBasicProductsBySearchTerm(searchTerm, language, market, limit, 0, productFields, productResultTypes, orderTypes)
                    .then(function (response) {
                        let products = response.products;
                        let productResults = [];
                        for (let i = 0; i < products.length; i++) {
                            let addProduct = createProductFromBasicProduct(products[i]);
                            productResults.push(addProduct);
                        }
                        resolve({"totalNumFound": response.totalNumFound, "products": productResults});
                    }, function (err) {
                        reject(err);
                    });
            });
        },

        /**
         * Search
         *
         * @param {string} searchTerm - Examples: Age, Age*, *Age*, etc.
         * @param {number[]} [productFields=[1,2,3,6,7,8,9,10,11]] - Example: [ProductSearchFieldsEnum.SKU, ProductSearchFieldsEnum.TITLE]
         * @param {string} [sortOrder=none]   - Example: ProductSortOrderEnum.ASCENDING
         * @param {number} [sortField=2]   - Example: ProductSearchFieldsEnum.TITLE
         * @param {number} [productResultTypes=1] - Example: ProductResultsEnum.PRODUCTS_AND_BASE_PRODUCTS -- default: ProductResultsEnum.ALL_PRODUCTS
         * @param {string[]} [orderTypes=["adr", "order"]] - Example: [OrderType.ADR, OrderType.ORDER, OrderType.ZPO] -- default: ["adr", "order"]
         * @param {string} [market=util.countryCode] - Example: "US" -- default: current page's market
         * @param {string} [language=util.languageCode]   - Example: "en" -- default: current page's language
         * @param {number} [rows=1000] - Example: 200 -- Maximum number of results that should be returned. This can be used to lazy load as needed
         * @param {number} [startIndex=0] - Example: 0 -- The result index to start returning from. Used with rows, can be used to lazy load
         *
         * @returns A promise to the result which contains a list of products that match the searchTerm.  The object
         *          returned can be added immediately to the cart
         */
        search: function(searchTerm, productFields, sortOrder, sortField, productResultTypes, orderTypes,  market, language, rows, startIndex) {
            //SET DEFAULT VALUES FOR OPTIONAL FIELDS
            if (typeof(productFields) === "undefined" || productFields === null) {
                productFields = [];
                productFields.push(ProductSearchFieldsEnum.SKU);
                productFields.push(ProductSearchFieldsEnum.TITLE);
                productFields.push(ProductSearchFieldsEnum.DESCRIPTION);
                productFields.push(ProductSearchFieldsEnum.LONG_DESCRIPTION);
                productFields.push(ProductSearchFieldsEnum.CATEGORIES);
                productFields.push(ProductSearchFieldsEnum.SEARCH_TERMS);
                productFields.push(ProductSearchFieldsEnum.BENEFITS);
                productFields.push(ProductSearchFieldsEnum.USAGE);
                productFields.push(ProductSearchFieldsEnum.KEY_INGREDIENTS);
            }
            if (typeof(sortOrder) === "undefined" || sortOrder === null)
                sortOrder = ProductSortOrderEnum.NONE;
            if (typeof(sortField) === "undefined" || sortField === null)
                sortField = ProductSearchFieldsEnum.TITLE;
            if (typeof(productResultTypes) === "undefined" || productResultTypes === null)
                productResultTypes = ProductResultsEnum.ALL_PRODUCTS;
            if (typeof(orderTypes) === "undefined" || orderTypes === null)
                orderTypes = [OrderType.ADR, OrderType.ORDER];
            if (typeof(market) === "undefined" || market === null) {
                market = util.countryCode;
            }
            if (typeof(language) === "undefined" || language === null) {
                language = util.languageCode;
            }
            if (typeof(rows) === "undefined" || rows === null) {
                rows = 1000;
            }
            if (typeof(startIndex) === "undefined" || rows === null) {
                startIndex = 0;
            }

            return new Promise((resolve, reject) => {
                searchTerm = removeSpecialCharacters(SynonymService.getSynonym(searchTerm));
                if (productResultTypes === ProductResultsEnum.PRODUCTS_AND_VARIANT_PRODUCTS) {
                    SolrQueryService.getProductsAndVariantsBySearchTerm(searchTerm, language, market, rows, startIndex, productFields, orderTypes)
                        .then(function (response) {
                            processSolrQueryResponse(
                                response, market, language, productResultTypes, orderTypes, sortOrder, sortField, resolve
                            );
                        }, function (err) {
                            reject(err);
                        });
                } else if (productResultTypes === ProductResultsEnum.PRODUCTS_AND_BASE_PRODUCTS) {
                    SolrQueryService.getProductsAndBasesBySearchTerm(searchTerm, language, market, rows, startIndex, productFields, orderTypes)
                        .then(function (response) {
                            processSolrQueryResponse(response, market, language, productResultTypes, orderTypes, sortOrder, sortField)
                                .then(function(processed) {
                                    getVariantsForProducts(processed.products, market, language, resolve, processed.totalNumFound);
                                });
                        }, function (err) {
                            reject(err);
                        });
                } else if (productResultTypes === ProductResultsEnum.BASE_PRODUCTS) {
                    SolrQueryService.getAllBaseProducts(language, market, rows, startIndex)
                        .then(function (response) {
                            processSolrQueryResponse(response, market, language, productResultTypes, orderTypes, sortOrder, sortField)
                                .then(function(processed) {
                                    getVariantsForProducts(processed.products, market, language, resolve, processed.totalNumFound);
                                });
                        }, function (err) {
                            reject(err);
                        });
                }  else if (productResultTypes === ProductResultsEnum.VARIANT_PRODUCTS) {
                    SolrQueryService.getAllVariantProducts(language, market, rows, startIndex)
                        .then(function (response) {
                            processSolrQueryResponse(
                                response, market, language, productResultTypes, orderTypes, sortOrder, sortField, resolve
                            );
                        }, function (err) {
                            reject(err);
                        });
                } else {
                    SolrQueryService.getAllProductsBySearchTerm(searchTerm, language, market, rows, startIndex, productFields, orderTypes)
                        .then(function (response) {
                            processSolrQueryResponse(response, market, language, productResultTypes, orderTypes, sortOrder, sortField)
                                .then(function(processed) {
                                    getVariantsForProducts(processed.products, market, language, resolve, processed.totalNumFound);
                                });
                        }, function (err) {
                            reject(err);
                        });
                }
            });
        },

        /**
         * Find Products Matching SKU Array
         *
         * @param skuArray - Examples: ["01550004"], ["01550004", "01001457"]
         * @param market - Example: "US" -- default: current page's market
         * @param language   - Example: "en" -- default: current page's language
         *
         * @returns A promise to the result which contains a list of products that have SKUs contained in the skuArray
         */
        getProductsFromSKUs: function(skuArray, market, language) {
            return new Promise((resolve) => {
                doGetProductsFromSKUs(skuArray, market, language)
                    .then(function (products) {
                        return getVariantsForProducts(products, market, language, resolve, products.length);
                    });
            });
        },

        /**
         * Get the enum holding the options for product search fields
         *
         * @returns an enum with the product search fields
         */
        getSearchFieldsEnum: function() {
            return ProductSearchFieldsEnum;
        },

        /**
         * Get the enum holding the options for product sort order
         *
         * @returns an enum with the product sort order
         */
        getSortOrderEnum: function() {
            return ProductSortOrderEnum;
        },

        /**
         * Get the enum holding the options for product sort type
         *
         * @returns an enum with the product sort type
         */
        getSortTypeEnum: function() {
            return ProductSortTypeEnum;
        },

        /**
         * Get the enum holding the options for product result types
         *
         * @returns an enum with the product result types
         */
        getResultTypesEnum: function() {
            return ProductResultsEnum;
        },

        /**
         * Get the enum holding the options for product order types
         *
         * @returns an enum with the product order types
         */
        getOrderTypesEnum: function() {
            return OrderType;
        }
    };

    //---------------------------------------------------------------------------
    //
    //                           Private Methods
    //
    //---------------------------------------------------------------------------

    const processSolrQueryResponse = function(response, market, language, productResultTypes, orderTypes, sortOrder, sortField, resolve) {
        if (response) {
            return new Promise((resolveCont/*, rejectCont*/) => {
                let products = response.products;
                let skuArray = [];

                for (let i = 0; i < products.length; i++) {
                    skuArray.push(products[i].getSku());
                }

                if (skuArray.length > 0) {
                    let productResults = [];
                    let marketChild;
                    for (let i = 0; i < products.length; i++) {
                        marketChild = products[i].getMarketChild(market);
                        if (searchedProductIsBase(products[i], productResultTypes) || (marketChild.getPurchasable() &&
                            existsInArray(marketChild.getOrderType(), orderTypes))) {
                            let productData = createDataFromSolrSearch(products[i], market, language);
                            if (productData) {
                                let addProduct = new Product(productData);

                                const siteUrl = ConfigService.getMarketConfig().siteUrl;
                                const isEdge = BrowserDetection.isEdge();
                                const isFirefox = BrowserDetection.isFirefox();

                                // set the base url for images, etc
                                addProduct.setBaseUrl(siteUrl);

                                // fix image paths for non-edge/firefox
                                if (!isEdge && !isFirefox) {
                                    addProduct.fullImage = addProduct.fullImage
                                        ? `${addProduct.fullImage.split("?")[0]}?format=pjpg`
                                        : undefined;
                                    addProduct.thumbnail = addProduct.thumbnail
                                        ? `${addProduct.thumbnail.split("?")[0]}?format=pjpg`
                                        : undefined;
                                }
                                
                                // set price type
                                const priceType = ConfigService.getMarketConfig().showWholeSalePricing && !AccountManager.isLoggedIn()
                                    ? PriceType.WWHL
                                    : PriceType.RTL;
                                addProduct.setPriceAndPvFromType(priceType);
                                productResults.push(addProduct);
                            }
                        }
                    }
                    productResults = sortProducts(productResults, sortOrder, productFieldAsText(sortField));
                    if (resolve)
                        resolve({"totalNumFound": response.totalNumFound, "products": productResults});
                    else
                        resolveCont({"totalNumFound": response.totalNumFound, "products": productResults});
                } else {
                    if (resolve)
                        resolve({"totalNumFound": 0, "products": []});
                    else resolveCont({"totalNumFound": 0, "products": []});
                }
            });
        } else {
            if (resolve)
                resolve({"totalNumFound": 0, "products": []});
            // else resolveCont([]);
        }
    }

    // const productHasValidStatus = function(productData) {
    //     if ((typeof(productData.status) === 'undefined'
    //             || productData.status == "RELEASED_FOR_SALE")
    //             && (productData.availableQuantity > 0
    //             || productData.backordered)
    //             && (productData.SKU && productData.price)) {
    //         return true;
    //     } else {
    //         return false;
    //     }
    // }

    const searchedProductIsBase = function(productData, productResultTypes) {
        if ((productResultTypes === ProductResultsEnum.PRODUCTS_AND_BASE_PRODUCTS
                || productResultTypes === ProductResultsEnum.BASE_PRODUCTS
                || productResultTypes === ProductResultsEnum.ALL_PRODUCTS)
                && isBaseSku(productData.SKU ? productData.SKU : productData.getSku())) {
            return true;
        } else {
            return false;
        }
    }

    const isBaseSku = function(sku) {
        return sku.length >= 8 && sku.substring(2, 4) === '55';
    }

    const existsInArray = function(searchArray, valuesArray) {
        if (searchArray && valuesArray) {
            return valuesArray.some(function (v) {
                return searchArray.indexOf(v) >= 0;
            });
        }
        return false;
    }

    //Converts a ProductSearchFieldsEnum to the text we use in solr
    // const fieldAsText = function(field) {
    //     let fields = ["", "sku", "name", "shortDescription", "products", "ingredients", "categories", "searchTerms", "keyIng", "longDescription"];
    //     return fields[field];
    // }

    //Converts a ProductSearchFieldsEnum to the text we use in Product
    const productFieldAsText = function(field) {
        let fields = ["", "sku", "title", "shortDescr", "products", "ingredients", "categories", "searchTerms", "keyIng", "longDescr"];
        return fields[field];
    }

    //Removes the count objects from the quickSearch response so we can have only the text that matches
    /*
    const removeCountsfromSolrResponse = function (solrResponse) {
        let prettyResponse = [];

        solrResponse.forEach(function (value) {
            if (typeof(value)!=='number') {
                prettyResponse.push(value);
            }
        });
        return prettyResponse;
    }
    */

    const createDataFromSolrSearch = function(solrData, market, language) {
        let data = {},
            langObj = solrData.getLanguageChild(language),
            marketObj = solrData.getMarketChild(market);

        if (marketObj !== null) {
            data["sku"] = solrData.getSku();
            data["fullImage"] = marketObj.getFullImageUrl();
            data["thumbnail"] = marketObj.getImageUrl() + ".160.160.png";
            // AEM-8320 - it returns two BP (08007070 and 08129080), 08129080 does not have any translation yet.
            // Note that langObj can be null (have a BP that does not have any translation)
            if (langObj) {
                data["language"] = language;
                data["title"] = langObj.getTitle();
                data["shortDescr"] = langObj.getDescription();
                data["longDescr"] = langObj.getLongDescription();
                data["ingredients"] = langObj.getIngredients();
                data["salesLabel"] = langObj.getSalesLabel();
                if (langObj.isBaseProduct()) {
                    data["variants"] = {};
                    let variantSKUs = langObj.getVariants();
                    for (let i = 0; i < variantSKUs.length; i++) {
                        data["variants"][variantSKUs[i]] = {};
                    }
                }
                if (langObj.isVariantProduct()) {
                    data["baseSku"] = langObj.getBaseSku();
                }
            }
            data["cntryCd"] = market;
            data["searchScore"] = solrData.getSearchScore();

            if (marketObj.getOrderType()) {
                data["orderTypes"] = marketObj.getOrderType();
            }
            if (marketObj.getCustType()) {
                data["custTypes"] = marketObj.getCustType();
            }
            if (langObj.getVariantType().length > 0) {
                data["variantType"] = langObj.getVariantType();
            }
            if (langObj.getVariantShade().length > 0) {
                data["shade"] = langObj.getVariantShade();
            }
            if (langObj.getVariantsLabel().length > 0) {
                data["variantsLabel"] = langObj.getVariantsLabel();
            }
            if (langObj.getVariantDropdownPlaceholder().length > 0) {
                data["variantDropdownPlaceholder"] = langObj.getVariantDropdownPlaceholder();
            }
            if (langObj.getVariantDropdownLabel().length > 0) {
                data["variantDropdownLabel"] = langObj.getVariantDropdownLabel();
            }
        }

        return data;
    }

    const getVariantsForProducts = function (productArray, market, language, resolve, totalNumFound) {
        let variantQueue = 0;
        for (let i = 0; i < productArray.length; i++) {
            if (productArray[i].isBase()) {
                variantQueue++;
                createVariantsObjectFromArray(productArray[i].variantSkus(), market, language, i)
                    .then(function (variantData) {
                        variantQueue--;
                        productArray[variantData.index]["variants"] = variantData.variants;
                        if (variantQueue == 0)
                            resolve({"totalNumFound": totalNumFound, "products": productArray});
                    });
            }
        }
        if (variantQueue == 0)
            resolve({"totalNumFound": totalNumFound, "products": productArray});
    }

    const createVariantsObjectFromArray = function(variantSkuArray, market, language, index) {
        return new Promise((resolve/*, reject*/) => {
            let variantData = {index: index};
            let variants = {};
            doGetProductsFromSKUs(variantSkuArray, market, language)
                .then(function (response) {
                    response.find(function(product) {
                        for (let i = 0; i < variantSkuArray.length; i++) {
                            if (product.sku == variantSkuArray[i]) {
                                variants[product.sku] = product;
                            }
                        }
                    });
                    variantData.variants = variants;
                    resolve (variantData);
                });
        });
    }

    const sortProducts = function(products, sortOrder, sortField) {
        if (sortOrder == ProductSortOrderEnum.DESCENDING) {
            products.sort(function(a, b) {
                let x = a[sortField].toLowerCase(); let y = b[sortField].toLowerCase();
                return ((x > y) ? -1 : ((x < y) ? 1 : 0));
            });
        } else if (sortOrder == ProductSortOrderEnum.ASCENDING) {
            products.sort(function(a, b) {
                let x = a[sortField].toLowerCase(); let y = b[sortField].toLowerCase();
                return ((x < y) ? -1 : ((x > y) ? 1 : 0));
            });
        } else {  //SORT BY SEARCH SCORE DESC (Changed default in CX12-3198)
            products.sort(function(a, b) {
                let x = a.searchScore; let y = b.searchScore;
                return ((x > y) ? -1 : ((x < y) ? 1 : 0));
            });
        }
        return products;
    }

    const removeSpecialCharacters = function(term) {
        let specialCharacters = ['*', '$', '\\', '+', '&', '!', '~', '?', '"', ';', '\t', '\r', '\n' ];
        for (let i = 0; i < specialCharacters.length; i++) {
            let index = term.indexOf(specialCharacters[i]);
            if (index > 0)
                term = term.substring(0, index);
        }
        return term;
    }

    /**
     * Orders a list of products by SKU matching the order of SKUs in skuArray
     * @param products - List of Products to be ordered
     * @param skuArray - List of SKUs in the order that the return object should be
     * @returns {*|promise}
     */
    const orderProductsBySkuArray = function(products, skuArray) {
        let prods = [];
        for (let i = 0; i < skuArray.length; i++)
        {
            for (let j = 0; j < products.length; j++) {
                if (skuArray[i] == products[j].sku)
                    prods.push(products[j]);
            }
        }
        return prods;
    }

    const doGetProductsFromSKUs = function(skuArray, market, language) {
        return new Promise((resolve/*, reject*/) => {
            if (typeof(skuArray) === "undefined" || skuArray === null || skuArray.length < 1) {
                resolve([]);
            }
            if (typeof(market) === "undefined" || market === null) {
                market = util.countryCode;
            }
            if (typeof(language) === "undefined" || language === null) {
                language = util.languageCode;
            }

            SolrQueryService.getProductsBySKUArray(skuArray, language, market)
                .then(function (response) {
                    if (response) {
                        gpfslCount = 0;

                        let _skuArray = skuArray;
                        let products = response;

                        if (products.length > 0) {
                            let productResults = [];
                            let marketChild;
                            for (let i = 0; i < products.length; i++) {
                                marketChild = products[i].getMarketChild(market);
                                if (isBaseSku(products[i].getSku()) || (marketChild && marketChild.getPurchasable())) {
                                    let productData = createDataFromSolrSearch(products[i], market, language);
                                    if (productData) {
                                        let addProduct = new Product(productData);

                                        const siteUrl = ConfigService.getMarketConfig().siteUrl;
                                        const isEdge = BrowserDetection.isEdge();
                                        const isFirefox = BrowserDetection.isFirefox();

                                        // set the base url for images, etc
                                        addProduct.setBaseUrl(siteUrl);

                                        // fix image paths for non-edge/firefox
                                        if (!isEdge && !isFirefox) {
                                            addProduct.fullImage = addProduct.fullImage
                                                ? `${addProduct.fullImage.split("?")[0]}?format=pjpg`
                                                : undefined;
                                            addProduct.thumbnail = addProduct.thumbnail
                                                ? `${addProduct.thumbnail.split("?")[0]}?format=pjpg`
                                                : undefined;
                                        }
                                
                                        const priceType = ConfigService.getMarketConfig().showWholeSalePricing && !AccountManager.isLoggedIn()
                                            ? PriceType.WWHL
                                            : PriceType.RTL;
                                        addProduct.setPriceAndPvFromType(priceType);
                                        productResults.push(addProduct);
                                    }
                                }
                            }
                            productResults = orderProductsBySkuArray(productResults, _skuArray);
                            resolve(productResults);
                        } else {
                            resolve([]);
                        }
                    } else {
                        if (gpfslCount != 1) {
                            gpfslCount = 1;
                            resolve(doGetProductsFromSKUs(skuArray, market, "en"));
                        }
                        else {
                            gpfslCount = 0;
                            console.log("Error getting products from SKUs");
                            resolve([]);
                        }
                    }
                }, function (err) {
                    console.log("Error retrieving products from Solr");
                    console.log(err);
                    resolve([]);
                });
        });
    }

    const createProductFromBasicProduct = function(basicProduct) {
        let siteUrl = ConfigService.getMarketConfig().siteUrl
        let productData = {};
        if (basicProduct.sku) {
            if (typeof(basicProduct.sku) === 'object') {
                productData.sku = basicProduct.sku[0];
            } else {
                productData.sku = basicProduct.sku;
            }
            // eslint-disable-next-line
            productData.thumbnail = siteUrl + "/content/products/" + productData.sku.substring(0,2) +"/" + productData.sku.substring(2,4) + "/" + productData.sku.substring(4,6) +"/" + productData.sku +"/_jcr_content/fullImage.img.160.160.png"
        }
        if (basicProduct.name) {
            if (typeof(basicProduct.name) === 'object') {
                productData.title = basicProduct.name[0];
            } else {
                productData.title = basicProduct.name;
            }
        }
        if (basicProduct.fullImage) {
            if (typeof(basicProduct.fullImage) === 'object') {
                productData.fullImage = basicProduct.fullImage[0];
            } else {
                productData.fullImage = basicProduct.fullImage;
            }
        }

        if (basicProduct.isExclusive) {
            if (typeof(basicProduct.isExclusive) === 'object') {
                productData.isExclusive = basicProduct.isExclusive[0];
            } else {
                productData.isExclusive = basicProduct.isExclusive;
            }
        }

        return new Product(productData);
    }

    return publicMethods;
}();

export default productSearchService;
